import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { Network } from '@capacitor/network';
import { Share } from '@capacitor/share';
import { StatusBar, Style } from '@capacitor/status-bar';
import type { AppNotification } from './types';

export const isNative = Capacitor.isNativePlatform();
export const NOTIFICATION_CHANNEL_ID = 'lspd-events';

function parseDeepLink(raw: string): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw);
    const target = url.searchParams.get('target');
    if (target) return target.startsWith('#') ? target : `#/${target.replace(/^\/+/, '')}`;
    if (url.protocol === 'serverassistant:') {
      const path = (url.host || url.pathname.replace(/^\/+/, '')).trim();
      if (path && path !== 'open') return `#/${path.replace(/^#?\/?/, '')}`;
    }
  } catch {
    // Fall through to the hash form.
  }
  return raw.startsWith('#') ? raw : null;
}

export async function prepareNativeNotifications() {
  if (!isNative) return 'unsupported';
  try {
    await LocalNotifications.createChannel({
      id: NOTIFICATION_CHANNEL_ID,
      name: '服务器小助手通知',
      description: '新委托、公告、拍卖和交易提醒',
      importance: 4,
      visibility: 1,
      vibration: true,
      lights: true,
      lightColor: '#19c8b9'
    });
  } catch {
    // Channel creation is Android-only; ignore on other platforms.
  }
  const permission = await LocalNotifications.checkPermissions();
  return permission.display;
}

export async function requestNativeNotificationPermission() {
  if (!isNative) return 'unsupported';
  await prepareNativeNotifications();
  const permission = await LocalNotifications.requestPermissions();
  return permission.display;
}

export async function notifyNative(notification: AppNotification, unreadCount: number) {
  if (!isNative) return;
  try {
    const permission = await LocalNotifications.checkPermissions();
    if (permission.display !== 'granted') return;
    await LocalNotifications.schedule({
      notifications: [{
        id: notification.seq,
        title: notification.title,
        body: notification.body || '点击查看详情',
        ...(notification.body ? { largeBody: notification.body } : {}),
        ...(unreadCount > 1 ? { summaryText: `${unreadCount} 条未读通知` } : {}),
        channelId: NOTIFICATION_CHANNEL_ID,
        smallIcon: 'ic_stat_lspd',
        iconColor: '#19c8b9',
        group: 'lspd-events',
        extra: { link: notification.link },
        autoCancel: true
      }]
    });
  } catch {
    // The in-app notification center remains available if the system prompt is denied.
  }
}

export function setupNativeShell(onNavigate: (link: string) => void) {
  if (!isNative) return () => {};

  const cleanups: Array<() => void> = [];
  const handles: Array<Promise<{ remove: () => Promise<void> }>> = [];

  if (Capacitor.getPlatform() === 'android') {
    void StatusBar.setStyle({ style: Style.Light });
    void StatusBar.setBackgroundColor({ color: '#f8f8f0' });
    void StatusBar.setOverlaysWebView({ overlay: false });
  }

  handles.push(App.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) window.history.back();
    else void App.exitApp();
  }));
  handles.push(App.addListener('appUrlOpen', event => {
    const link = parseDeepLink(event.url);
    if (link) onNavigate(link);
  }));
  handles.push(App.addListener('appStateChange', ({ isActive }) => {
    if (isActive) window.dispatchEvent(new Event('lspd:resume'));
  }));
  handles.push(LocalNotifications.addListener('localNotificationActionPerformed', event => {
    const link = event.notification.extra?.link;
    if (typeof link === 'string') onNavigate(link);
  }));
  handles.push(Network.addListener('networkStatusChange', status => {
    window.dispatchEvent(new CustomEvent('lspd:network', { detail: status.connected }));
  }));

  void Network.getStatus().then(status => {
    window.dispatchEvent(new CustomEvent('lspd:network', { detail: status.connected }));
  }).catch(() => {});

  const hapticHandler = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target?.closest('button, .mobile-tabbar button, .side-nav-item')) return;
    void Haptics.impact({ style: ImpactStyle.Light }).catch(() => {});
  };
  document.addEventListener('click', hapticHandler, { passive: true });
  cleanups.push(() => document.removeEventListener('click', hapticHandler));

  let disposed = false;
  void Promise.all(handles).then(list => {
    if (disposed) {
      for (const handle of list) void handle.remove();
      return;
    }
    for (const handle of list) cleanups.push(() => void handle.remove());
  }).catch(() => {});

  return () => {
    disposed = true;
    for (const cleanup of cleanups) cleanup();
  };
}

export async function shareApp(): Promise<'shared' | 'copied' | 'failed'> {
  const text = '服务器小助手：https://lspd.baimao.wang';
  try {
    if (isNative) {
      await Share.share({ title: '服务器小助手', text, url: 'https://lspd.baimao.wang', dialogTitle: '分享服务器小助手' });
      return 'shared';
    }
    if (navigator.share) {
      await navigator.share({ title: '服务器小助手', text, url: 'https://lspd.baimao.wang' });
      return 'shared';
    }
    await navigator.clipboard.writeText(text);
    return 'copied';
  } catch {
    return 'failed';
  }
}
